import React, { useState, useEffect } from "react";
import { X, Plus, Mail, Loader2 } from "lucide-react";

// Fallback predefined conditions (used when API fails)
const FALLBACK_CONDITIONS = [
  { id: "severity-critical", label: "Severity = Critical", value: "Severity = Critical", category: "Severity" },
  { id: "severity-high", label: "Severity = High", value: "Severity = High", category: "Severity" },
  { id: "severity-medium", label: "Severity = Medium", value: "Severity = Medium", category: "Severity" },
  { id: "severity-low", label: "Severity = Low", value: "Severity = Low", category: "Severity" },
  { id: "failed-logins-5", label: "Failed logins > 5 in 5 minutes", value: "Failed logins > 5 in 5 minutes", category: "Authentication" },
  { id: "failed-logins-10", label: "Failed logins > 10 in 5 minutes", value: "Failed logins > 10 in 5 minutes", category: "Authentication" },
  { id: "malware-match", label: "Malware signature match", value: "Malware signature match", category: "Threat Detection" },
  { id: "data-transfer-500mb", label: "Data transfer > 500MB external", value: "Data transfer > 500MB external", category: "Data Loss Prevention" },
  { id: "data-transfer-1gb", label: "Data transfer > 1GB external", value: "Data transfer > 1GB external", category: "Data Loss Prevention" },
  { id: "unusual-login-location", label: "Unusual login location detected", value: "Unusual login location detected", category: "Authentication" },
  { id: "privilege-escalation", label: "Privilege escalation attempt", value: "Privilege escalation attempt", category: "Access Control" },
  { id: "brute-force", label: "Brute force attack detected", value: "Brute force attack detected", category: "Threat Detection" },
  { id: "phishing-detected", label: "Phishing attempt detected", value: "Phishing attempt detected", category: "Threat Detection" },
  { id: "unauthorized-access", label: "Unauthorized access attempt", value: "Unauthorized access attempt", category: "Access Control" },
];

export default function AlertRuleModal({ 
  isOpen, 
  onClose, 
  onSave, 
  rule, 
  mode,
  conditions: apiConditions,
  isConditionsLoading,
  workspaces: apiWorkspaces,
  isWorkspacesLoading,
  isSaving,
}) {
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    enabled: true,
    conditions: [],
    emails: [],
    workspaceIds: [],
  });

  const [newEmail, setNewEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [formError, setFormError] = useState("");

  // Use API conditions if available, otherwise use fallback
  const availableConditions = apiConditions && apiConditions.length > 0 
    ? apiConditions 
    : FALLBACK_CONDITIONS;

  // Use API workspaces if available
  const availableWorkspaces = apiWorkspaces && apiWorkspaces.length > 0 
    ? apiWorkspaces 
    : [];

  // Group conditions by category
  const groupedConditions = availableConditions.reduce((groups, condition) => {
    const category = condition.category || "Other";
    if (!groups[category]) {
      groups[category] = [];
    }
    groups[category].push(condition);
    return groups;
  }, {});

  // Initialize form when rule changes or modal opens
  useEffect(() => {
    if (rule && mode === "edit") {
      setFormData({
        name: rule.name || "",
        description: rule.description || "",
        enabled: rule.enabled ?? true,
        conditions: rule.conditions || [],
        emails: rule.emails || [],
        workspaceIds: rule.workspaceIds || [],
      });
    } else {
      setFormData({
        name: "",
        description: "",
        enabled: true,
        conditions: [],
        emails: [],
        workspaceIds: [],
      });
    }
    setNewEmail("");
    setEmailError("");
    setFormError("");
  }, [rule, mode, isOpen]);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleAddEmail = () => {
    const trimmedEmail = newEmail.trim();
    
    if (!trimmedEmail) {
      setEmailError("Please enter an email address");
      return;
    }
    
    if (!validateEmail(trimmedEmail)) {
      setEmailError("Please enter a valid email address");
      return;
    }
    
    if (formData.emails.includes(trimmedEmail)) {
      setEmailError("This email is already added");
      return;
    }

    setFormData((prev) => ({
      ...prev,
      emails: [...prev.emails, trimmedEmail],
    }));
    setNewEmail("");
    setEmailError("");
  };

  const handleRemoveEmail = (emailToRemove) => {
    setFormData((prev) => ({
      ...prev,
      emails: prev.emails.filter((email) => email !== emailToRemove),
    }));
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddEmail();
    }
  };

  const handleWorkspaceSelect = (selected) => {
    const ids = Array.isArray(selected) ? selected.map((w) => w.value) : [];
    setFormData((prev) => ({ ...prev, workspaceIds: ids }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError("");

    // Include any pending typed email before submit
    if (newEmail && !formData.emails.includes(newEmail.trim())) {
      const trimmedEmail = newEmail.trim();
      if (!validateEmail(trimmedEmail)) {
        setFormError("Please enter a valid email address");
        return;
      }
      setFormData((prev) => ({
        ...prev,
        emails: [...prev.emails, trimmedEmail],
      }));
      setNewEmail("");
    }
    
    // Validation
    if (!formData.name.trim()) {
      setFormError("Rule name is required");
      return;
    }
    
    if (!formData.conditions || formData.conditions.length === 0) {
      setFormError("Please select at least one condition");
      return;
    }

    const ruleData = {
      ...formData,
      id: rule?.id,
    };

    const result = await onSave(ruleData);
    
    if (!result.success) {
      setFormError(result.error || "Failed to save rule. Please try again.");
    }
  };

  if (!isOpen) return null;

  return (
    <div className="alert-rule-modal-overlay" onClick={onClose}>
      <div className="alert-rule-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="alert-rule-modal__header">
          <div className="alert-rule-modal__title-section">
            <h2 className="alert-rule-modal__title">
              {mode === "edit" ? "Edit Alert Rule" : "Create New Alert Rule"}
            </h2>
            <p className="alert-rule-modal__subtitle">
              {mode === "edit"
                ? "Modify the alert rule configuration below"
                : "Configure a new alert rule to monitor your security events"}
            </p>
          </div>
          <button 
            className="alert-rule-modal__close" 
            onClick={onClose}
            disabled={isSaving}
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <form className="alert-rule-modal__content" onSubmit={handleSubmit}>
          <div className="alert-rule-modal__form">
            {/* Form Error */}
            {formError && (
              <div className="alert-rule-modal__error">
                {formError}
              </div>
            )}

            {/* Title */}
            <div className="form-field">
              <label className="form-field__label">Rule Name *</label>
              <input
                type="text"
                className="form-field__input"
                placeholder="Enter rule name..."
                value={formData.name}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, name: e.target.value }))
                }
                disabled={isSaving}
                required
              />
            </div>

            {/* Description */}
            <div className="form-field">
              <label className="form-field__label">Description</label>
              <textarea
                className="form-field__textarea"
                placeholder="Enter a description for this rule..."
                value={formData.description}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, description: e.target.value }))
                }
                disabled={isSaving}
                rows={3}
              />
            </div>

            {/* Active Toggle */}
            <div className="form-field form-field--switch">
              <label className="form-field__label">Status</label>
              <div className="alert-rule-modal__toggle-row">
                <div className="switch">
                  <input
                    id="rule-enabled-toggle"
                    type="checkbox"
                    className="switch__input"
                    checked={formData.enabled}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, enabled: e.target.checked }))
                    }
                    disabled={isSaving}
                  />
                  <label
                    htmlFor="rule-enabled-toggle"
                    className="switch__label"
                  ></label>
                </div>
                <span className={`alert-rule-modal__status-text ${formData.enabled ? "alert-rule-modal__status-text--active" : ""}`}>
                  {formData.enabled ? "Active" : "Inactive"}
                </span>
              </div>
            </div>

            {/* Conditions Dropdown */}
            <div className="form-field">
              <label className="form-field__label">
                Condition *
                {isConditionsLoading && (
                  <Loader2 size={14} className="alert-rules__spinner" style={{ marginLeft: 8 }} />
                )}
              </label>
              <select
                multiple
                className="form-field__input form-field__select"
                value={formData.conditions}
                onChange={(e) => {
                  const selected = Array.from(e.target.selectedOptions).map((o) => o.value);
                  setFormData((prev) => ({ ...prev, conditions: selected }));
                }}
                disabled={isSaving || isConditionsLoading}
              >
                {Object.entries(groupedConditions).map(([category, conditions]) => (
                  <optgroup key={category} label={category}>
                    {conditions.map((condition) => (
                      <option key={condition.id} value={condition.value}>
                        {condition.label}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </div>

            {/* Email Addresses */}
            <div className="form-field">
              <label className="form-field__label">
                <Mail size={14} style={{ marginRight: "6px", verticalAlign: "middle" }} />
                Notification Emails
              </label>
              <div className="email-input-group">
                <input
                  type="email"
                  className="form-field__input"
                  placeholder="Enter email address..."
                  value={newEmail}
                  onChange={(e) => {
                    setNewEmail(e.target.value);
                    setEmailError("");
                  }}
                  onKeyPress={handleKeyPress}
                  disabled={isSaving}
                />
                <button
                  type="button"
                  className="btn btn--primary btn--icon"
                  onClick={handleAddEmail}
                  disabled={isSaving}
                >
                  <Plus size={16} />
                </button>
              </div>
              {emailError && <p className="form-field__error">{emailError}</p>}
              
              {formData.emails.length > 0 && (
                <div className="email-tags">
                  {formData.emails.map((email) => (
                    <div key={email} className="email-tag">
                      <span>{email}</span>
                      <button
                        type="button"
                        className="email-tag__remove"
                        onClick={() => handleRemoveEmail(email)}
                        disabled={isSaving}
                      >
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Workspace IDs */}
            <div className="form-field">
              <label className="form-field__label">
                Workspaces
                {isWorkspacesLoading && (
                  <Loader2 size={14} className="alert-rules__spinner" style={{ marginLeft: 8 }} />
                )}
              </label>
              <p className="form-field__description">
                Select the workspaces where this rule should be applied
              </p>
              <select
                multiple
                className="form-field__input form-field__select"
                value={formData.workspaceIds}
                onChange={(e) => {
                  const selected = Array.from(e.target.selectedOptions).map((o) => o.value);
                  handleWorkspaceSelect(selected.map((v) => ({ value: v })));
                }}
                disabled={isSaving || isWorkspacesLoading}
              >
                {availableWorkspaces.length === 0 && !isWorkspacesLoading ? (
                  <option value="">No workspaces available</option>
                ) : (
                  availableWorkspaces.map((w) => (
                    <option key={w.id || w.workspaceId} value={w.id || w.workspaceId}>
                      {w.name || w.workspaceName || "Workspace"}
                    </option>
                  ))
                )}
              </select>
            </div>
          </div>

          {/* Footer */}
          <div className="alert-rule-modal__footer">
            <button 
              type="button" 
              className="btn btn--outline" 
              onClick={onClose}
              disabled={isSaving}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="btn btn--primary"
              disabled={isSaving}
            >
              {isSaving ? (
                <>
                  <Loader2 size={16} className="alert-rules__spinner" />
                  {mode === "edit" ? "Saving..." : "Creating..."}
                </>
              ) : (
                mode === "edit" ? "Save Changes" : "Create Rule"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
